import api from './api';
import { Signal, ContextSource } from '../types/context.types';

export interface PrimerChip {
  templateId: string;
  label: string;
  prompt: string;
}

export interface Primer {
  chips: PrimerChip[];
  ghostText: string;
}

interface CacheEntry {
  payload: Primer;
  signalsHash: string;
  sourcesHash: string;
  generatedAt: number;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function djb2(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

export function hashSignals(signals: Signal[]): string {
  if (signals.length === 0) return '';
  const ids = signals
    .filter((s) => s.isActive !== false)
    .map((s) => s.id)
    .filter(Boolean)
    .sort();
  return djb2(ids.join('|'));
}

export function hashSources(sources: ContextSource[]): string {
  if (sources.length === 0) return '';
  const ids = sources
    .map((s) => s.id)
    .filter(Boolean)
    .sort();
  return djb2((ids as string[]).join('|'));
}

function cacheKey(projectId: string): string {
  return `primer_${projectId}`;
}

function readCache(projectId: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(cacheKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed?.payload || typeof parsed.generatedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(projectId: string, entry: CacheEntry): void {
  try {
    localStorage.setItem(cacheKey(projectId), JSON.stringify(entry));
  } catch {
    // Quota or serialization failure — silently skip; the network path still works.
  }
}

class PrimerService {
  async getPrimer(
    projectId: string,
    signalsHash: string,
    sourcesHash: string
  ): Promise<Primer> {
    const cached = readCache(projectId);
    if (
      cached &&
      cached.signalsHash === signalsHash &&
      cached.sourcesHash === sourcesHash &&
      Date.now() - cached.generatedAt < CACHE_TTL_MS
    ) {
      return cached.payload;
    }

    try {
      const payload = await api.get<Primer>(
        `/api/context/primer/${encodeURIComponent(projectId)}`
      );
      const safe: Primer = {
        chips: Array.isArray(payload?.chips) ? payload.chips : [],
        ghostText: typeof payload?.ghostText === 'string' ? payload.ghostText : '',
      };
      writeCache(projectId, {
        payload: safe,
        signalsHash,
        sourcesHash,
        generatedAt: Date.now(),
      });
      return safe;
    } catch {
      return { chips: [], ghostText: '' };
    }
  }

  setCache(
    projectId: string,
    signalsHash: string,
    sourcesHash: string,
    payload: Primer
  ): void {
    writeCache(projectId, {
      payload,
      signalsHash,
      sourcesHash,
      generatedAt: Date.now(),
    });
  }
}

const primerService = new PrimerService();
export default primerService;
